// Settings (wave 12, account track): account, privacy, playtime and data, notifications, connected accounts.
// Plain words, every switch saves at once, and nothing here pretends: each control maps to one stored setting.

import { accessToken, api, API_BASE, ApiError, needsSignIn } from "../api.ts";
import { clear, errorBox, h, link, toast } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { signedIn, signOut } from "../session.ts";
import { signinMethodsSection } from "./me-settings-methods.ts";
import { prefsOf, type Switches } from "../notify.ts";
import { accountStyles, ARCHETYPES, GENRES, getFullMe, patchMe, type Audience, type FullMe, type Privacy } from "./me-kit.ts";

const AUDIENCE: [Audience, string][] = [
  ["everyone", "Everyone"],
  ["friends", "Friends"],
  ["nobody", "Only me"],
];

/** What each privacy setting covers, in the words players read. */
export const PRIVACY_ROWS: [Exclude<keyof Privacy, "friendRequests">, string, string][] = [
  ["profile", "Your profile", "Your name and avatar on your profile page and next to your clips."],
  ["library", "Games you love", "The games you heart and the creators you follow, on your profile."],
  ["playtime", "Your playtime", "How long you've played each game."],
  ["presence", "What you're playing now", "Whether friends see you online and in which game."],
];

const SECTIONS: [string, string][] = [
  ["account", "Account"],
  ["signin-methods", "Sign-in methods"],
  ["privacy", "Privacy"],
  ["data", "Playtime and data"],
  ["notifications", "Notifications"],
  ["connected", "Connected accounts"],
];

function sw(on: boolean, label: string, save: (v: boolean) => Promise<unknown>): HTMLElement {
  const b = h("button", { class: "acct-switch", role: "switch", type: "button", "aria-checked": String(on), "aria-label": label });
  b.addEventListener("click", async () => {
    const v = b.getAttribute("aria-checked") !== "true";
    b.setAttribute("aria-checked", String(v));
    b.setAttribute("disabled", "");
    try {
      await save(v);
      toast("Saved");
    } catch (e) {
      b.setAttribute("aria-checked", String(!v));
      toast(e instanceof Error ? e.message : "Couldn't save that");
    } finally {
      b.removeAttribute("disabled");
    }
  });
  return b;
}

const row = (title: string, text: string | null, control: HTMLElement | null) =>
  h("div", { class: "set-row" }, h("div", null, h("b", null, title), text ? h("p", null, text) : null), control);

function select<T extends string>(id: string, value: T, options: [T, string][], save: (v: T) => Promise<unknown>): HTMLSelectElement {
  const s = h("select", { id, class: "set-select" }, options.map(([v, t]) => h("option", { value: v, selected: v === value }, t))) as HTMLSelectElement;
  s.addEventListener("change", async () => {
    try {
      await save(s.value as T);
      toast("Saved");
    } catch (e) {
      s.value = value;
      toast(e instanceof Error ? e.message : "Couldn't save that");
    }
  });
  return s;
}

export async function settingsPage(_p: unknown, query: URLSearchParams = new URLSearchParams()): Promise<HTMLElement> {
  setTitle("Settings");
  accountStyles();
  if (!signedIn()) {
    navigate(`/signin?next=${encodeURIComponent("/me/settings")}`, true);
    return h("div");
  }
  const body = h("div", { class: "set-main" }, h("p", { class: "dim" }, "Loading your settings…"));
  const nav = h("nav", { class: "set-nav", "aria-label": "Settings" }, SECTIONS.map(([id, t]) => h("a", { href: `#${id}` }, t)));
  const root = h("div", { class: "acct acct-page" }, h("header", { class: "set-head" }, h("p", { class: "kicker" }, "Your space"), h("h1", null, "Settings")), h("div", { class: "set-grid" }, nav, body));
  let me: FullMe;
  try {
    me = await getFullMe();
  } catch (e) {
    clear(body, errorBox(e));
    return root;
  }
  const save = async (patch: Record<string, unknown>) => (me = await patchMe(patch));
  // back from linking Discord or GitHub: the code finishes here, then leaves the address bar
  const oauth = query.get("oauth");
  const oauthError = query.get("oauth_error");
  if (oauth || oauthError) history.replaceState(null, "", "/me/settings#signin-methods");
  clear(body, account(me, save), signinMethodsSection(oauth, oauthError), privacy(me, save), data(me, save), notifications(me, save), connected());
  return root;
}

function account(me: FullMe, save: (p: Record<string, unknown>) => Promise<FullMe>): HTMLElement {
  const display = h("input", { id: "set-name", type: "text", maxlength: 40, value: me.displayName ?? "", placeholder: "Your name" }) as HTMLInputElement;
  const handle = h("input", { id: "set-handle", type: "text", maxlength: 20, value: me.handle ?? "", placeholder: "yourname", spellcheck: "false" }) as HTMLInputElement;
  const err = h("div", { "aria-live": "polite" });
  const form = h(
    "form",
    {
      class: "set-form",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        clear(err);
        const patch: Record<string, unknown> = {};
        const d = display.value.trim();
        const hd = handle.value.trim().replace(/^@/, "");
        if (d !== (me.displayName ?? "")) patch.displayName = d || null;
        if (hd && hd !== me.handle) patch.handle = hd;
        if (!Object.keys(patch).length) return;
        try {
          await save(patch);
          toast("Saved");
        } catch (x) {
          const code = x instanceof ApiError ? x.code : "";
          clear(err, code === "handle_taken" ? h("p", { class: "error" }, `@${hd} is taken.`) : code === "invalid_handle" ? h("p", { class: "error" }, "Handles are 3–20 letters, digits or _, starting with a letter.") : errorBox(x));
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "set-name" }, "Name"), display),
    h("div", { class: "field" }, h("label", { for: "set-handle" }, "Handle"), handle),
    h("button", { class: "btn small", type: "submit" }, "Save"),
    err,
  );
  const outErr = h("div", { "aria-live": "polite" });
  const picks = [...(me.taste?.archetypes ?? []).map((id) => ARCHETYPES.find((a) => a.id === id)?.name ?? id), ...(me.taste?.genres ?? []).map((id) => GENRES.find(([g]) => g === id)?.[1] ?? id)];
  return h(
    "section",
    { id: "account", class: "set-card" },
    h("h2", null, "Account"),
    form,
    row("Email", me.email, null),
    row("Age", me.ageBand === "13-17" ? "13–17. Creator tools and selling open at 18. Your account moves up to 18+ by itself; there's nothing to fill in." : "18 or older.", null),
    row("What you like to play", picks.length ? picks.join(", ") : "Not picked yet. We use this to put your kind of games first.", link("/welcome?step=taste", { class: "btn small ghost" }, picks.length ? "Change" : "Pick")),
    row("Your public profile", me.handle ? `banger.sh/u/${me.handle}` : "Pick a handle to get a profile address.", me.handle ? link(`/u/${me.handle}`, { class: "btn small ghost" }, "View") : null),
    row(
      "Sign out",
      "Sign out here, or everywhere you're signed in (other browsers and the app).",
      h(
        "div",
        { class: "acct-row", style: { marginTop: "0" } },
        h("button", { class: "btn small ghost", type: "button", onclick: async () => (await signOut(), navigate("/")) }, "Sign out"),
        h(
          "button",
          {
            class: "btn small ghost",
            type: "button",
            onclick: async (e: Event) => {
              const b = e.currentTarget as HTMLButtonElement;
              b.disabled = true;
              clear(outErr);
              const r = await signOutEverywhere(() => api("accounts", "/auth/signout", { body: { all: true } }));
              b.disabled = false;
              if (r !== true) return void clear(outErr, h("p", { class: "error" }, "Couldn't sign you out everywhere. You're still signed in; try again."), errorBox(r));
              await signOut();
              navigate("/");
            },
          },
          "Everywhere",
        ),
      ),
    ),
    outErr,
  );
}

function privacy(me: FullMe, save: (p: Record<string, unknown>) => Promise<FullMe>): HTMLElement {
  const teen = me.ageBand === "13-17";
  return h(
    "section",
    { id: "privacy", class: "set-card" },
    h("h2", null, "Privacy"),
    h("p", { class: "dim" }, "Who sees what. ", teen ? "Your account starts private because you're under 18; you can open it up a little." : "Change any of it any time."),
    PRIVACY_ROWS.map(([key, title, text]) => row(title, text, select(`priv-${key}`, me.privacy[key], AUDIENCE, (v) => save({ privacy: { [key]: v } })))),
    row(
      "Friend requests",
      "Who can send you one. “Friends of friends” means you need a friend in common first; anyone you ask can still say yes.",
      select("priv-friendRequests", me.privacy.friendRequests, [["everyone", "Everyone"], ["friends_of_friends", "Friends of friends"], ["nobody", "Nobody"]], (v) => save({ privacy: { friendRequests: v } })),
    ),
  );
}

function data(me: FullMe, save: (p: Record<string, unknown>) => Promise<FullMe>): HTMLElement {
  const err = h("div", { "aria-live": "polite" });
  const download = h("button", { class: "btn small ghost", type: "button" }, "Download my data");
  download.addEventListener("click", async () => {
    download.setAttribute("disabled", "");
    try {
      const res = await api<Response>("accounts", "/me/export", { raw: true });
      if (!res.ok) throw new Error(`Couldn't make your file (${res.status}).`);
      const url = URL.createObjectURL(await res.blob());
      const a = h("a", { href: url, download: `banger-account-${me.id}.json` });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      clear(err, errorBox(e));
    } finally {
      download.removeAttribute("disabled");
    }
  });
  return h(
    "section",
    { id: "data", class: "set-card" },
    h("h2", null, "Playtime and data"),
    row("Count my playtime", "When you play in the Banger app we count the minutes you actively play each game. It puts your recent games first, and when creators get paid it decides how they share what Banger earns. Off: we stop counting, and what's already counted stays until you delete it with your account.", sw(me.consent.playtime, "Count my playtime", (v) => save({ consent: { playtime: v } }))),
    row("Usage statistics", "Anonymous numbers about how the app is used, so we can fix what's confusing.", sw(me.consent.analytics, "Usage statistics", (v) => save({ consent: { analytics: v } }))),
    row("Crash reports", "When the app crashes, send us what went wrong (no personal data).", sw(me.consent.crashReports, "Crash reports", (v) => save({ consent: { crashReports: v } }))),
    row("Your data", "Everything we keep about you, as one file: your account, library, hearts, follows, playtime and notifications.", download),
    err,
    deleteBox(me),
  );
}

function deleteBox(me: FullMe): HTMLElement {
  const err = h("div", { "aria-live": "polite" });
  const confirm = h("input", { id: "del-confirm", type: "text", placeholder: "delete my account", autocomplete: "off", spellcheck: "false" }) as HTMLInputElement;
  const go = h("button", { class: "btn small danger", type: "button" }, "Delete my account");
  go.addEventListener("click", async () => {
    if (confirm.value.trim().toLowerCase() !== "delete my account") return clear(err, h("p", { class: "error" }, "Type “delete my account” to confirm."));
    go.setAttribute("disabled", "");
    try {
      await api("accounts", "/me", { method: "DELETE", body: { confirm: me.id } });
      await signOut();
      navigate("/", true);
      toast("Your account is deleted.");
    } catch (e) {
      go.removeAttribute("disabled");
      clear(err, errorBox(e));
    }
  });
  return h(
    "details",
    { class: "set-danger" },
    h("summary", null, "Delete my account"),
    h("p", null, "This deletes your account, library, hearts, follows, playtime and notifications for good. Games you claimed go back to being unclaimed listings. It can't be undone."),
    h("div", { class: "field" }, h("label", { for: "del-confirm" }, "Type “delete my account”"), confirm),
    err,
    go,
  );
}

/** wave 13 (w13-notify): one switch per kind of notification, grouped as players think of them, and quiet hours. */
const NOTIFY_GROUPS: [string, [keyof Switches, string, string][]][] = [
  ["Events", [
    ["eventReminder", "15 minutes before an event you're going to", "We ask when you tap I'm going on an event. Turn it on here any time."],
    ["eventStarting", "An event is starting", "Events you're going to, and the big ones on Banger."],
  ]],
  ["Friends", [
    ["friendMessages", "A friend messages you", "In the app. One line per friend, never the message itself."],
    ["friends", "Friend requests and invites", "Requests, accepted requests and party invites."],
  ]],
  ["What you made and said", [
    ["noteShipped", "A note you left shipped", "When a creator fixes what you told them about."],
    ["replies", "Replies to your posts", "Replies to your posts and to your threads on game pages."],
    ["forks", "Your assets get forked or a change is offered", "And when a change you offered is merged or declined."],
  ]],
  ["Games and people", [
    ["gameReleased", "A game you're waiting for comes out", "Games on your waitlist, or that you hearted or follow."],
    ["gameUpdated", "A game you love gets an update", "Games you hearted or have in your library."],
    ["creatorShipped", "A creator you follow ships a game", "New games from creators you follow."],
    ["followReleases", "Someone you follow releases something", "Release posts from people you follow."],
    ["gameNews", "Devlogs and bug fixes", "From games you follow, and bugs you reported."],
  ]],
];

const TIMES: [string, string][] = Array.from({ length: 48 }, (_, i) => {
  const v = `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
  return [v, v] as [string, string];
});

function notifications(me: FullMe, save: (p: Record<string, unknown>) => Promise<FullMe>): HTMLElement {
  const p = prefsOf(me.notify);
  const minor = me.ageBand !== "18+";
  const groups = NOTIFY_GROUPS.map(([title, rows]) =>
    h("div", { class: "set-group" }, h("h3", { class: "set-sub" }, title), rows.map(([key, t, text]) => row(t, text, sw(p[key], t, (v) => save({ notify: { [key]: v } }))))),
  );
  // quiet hours: a switch, and the two times when it's on
  let quiet = p.quiet;
  const times = h("div", { class: "set-times" });
  const paintTimes = () => {
    clear(times);
    if (!quiet) return;
    const q = quiet;
    times.append(
      h("label", { for: "quiet-from" }, "From"),
      select("quiet-from", q.from, TIMES, async (v) => ((quiet = { ...q, from: v }), save({ notify: { quiet } }))),
      h("label", { for: "quiet-to" }, "to"),
      select("quiet-to", q.to, TIMES, async (v) => ((quiet = { ...q, to: v }), save({ notify: { quiet } }))),
    );
  };
  paintTimes();
  const quietSw = sw(!!quiet, "Quiet hours", async (on) => {
    quiet = on ? quiet ?? { from: "22:00", to: "08:00" } : null;
    await save({ notify: { quiet } });
    paintTimes();
  });
  return h(
    "section",
    { id: "notifications", class: "set-card" },
    h("style", null, `#notifications .set-group { margin-top: 22px; } #notifications .set-sub { margin: 0 0 2px; font-size: 13px; font-weight: 650; letter-spacing: .06em; text-transform: uppercase; color: var(--a-dim); } #notifications .set-times { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding-bottom: 8px; } #notifications .set-times:empty { display: none; } #notifications .set-times label { color: var(--a-dim); font-size: 14px; }`),
    h("h2", null, "Notifications"),
    h("p", { class: "dim" }, "What reaches your inbox here and in the app, and what the app shows on your desktop. We don't send notification emails."),
    minor ? h("p", { class: "dim" }, "Players you don't know can't send you notifications. A friend request only notifies you when you share a friend. A reply to your post only notifies you when it's from a friend, and a reply on a game page only when it's from the game's creator.") : null,
    ...groups,
    h(
      "div",
      { class: "set-group" },
      h("h3", { class: "set-sub" }, "Quiet hours"),
      row("No desktop notifications at night", "Your inbox still fills up. You'll see it all in the morning. Uses this device's clock.", quietSw),
      times,
    ),
  );
}

// ---- connected accounts: X (social's Bangers protocol) ----------------------------------------------------------

interface XStart {
  code: string;
  tweet_text: string;
  expires_at: number;
}

function connected(): HTMLElement {
  const box = h("div", null, h("p", { class: "dim" }, "Checking…"));
  const paint = async () => {
    let linked = false;
    try {
      linked = (await api<{ linked: boolean }>("social", "/x/link")).linked;
    } catch (e) {
      return clear(box, row("X", "We can't reach the friends service right now.", null), errorBox(e));
    }
    if (linked) return clear(box, xLinked(paint));
    clear(box, xStart(paint));
  };
  void paint();
  return h(
    "section",
    { id: "connected", class: "set-card" },
    h("h2", null, "Connected accounts"),
    h("p", { class: "dim" }, "Find the people you already follow. We never log in to X or read your posts, and we keep only scrambled IDs."),
    box,
  );
}

function xStart(repaint: () => Promise<void>): HTMLElement {
  const area = h("div");
  const begin = h("button", { class: "btn small", type: "button" }, "Connect X");
  begin.addEventListener("click", async () => {
    begin.setAttribute("disabled", "");
    try {
      const s = await api<XStart>("social", "/x/verify/start", { body: {} });
      const url = h("input", { id: "x-url", type: "text", placeholder: "https://x.com/you/status/…", autocomplete: "off" }) as HTMLInputElement;
      const err = h("div", { "aria-live": "polite" });
      const check = h("button", { class: "btn small", type: "button" }, "Check my post");
      check.addEventListener("click", async () => {
        clear(err);
        try {
          const r = await api<{ status: string }>("social", "/x/verify/check", { body: { tweet_url: url.value.trim() } });
          if (r.status === "verified") return void repaint();
          clear(err, h("p", { class: "dim" }, "We haven't seen it yet. Posts can take a few minutes to show; try again shortly."));
        } catch (e) {
          clear(err, errorBox(e));
        }
      });
      clear(
        area,
        h("ol", { class: "set-steps" },
          h("li", null, "Post this from your X account (it proves the account is yours): ", h("code", null, s.tweet_text), " ", h("a", { href: `https://x.com/intent/tweet?text=${encodeURIComponent(s.tweet_text)}`, target: "_blank", rel: "noopener noreferrer" }, "Post on X")),
          h("li", null, "Paste the link to your post:", h("div", { class: "field" }, url)),
        ),
        check,
        err,
        h("p", { class: "fine" }, "The code works for 24 hours. You can delete the post once we've checked it."),
      );
    } catch (e) {
      begin.removeAttribute("disabled");
      clear(area, errorBox(e));
    }
  });
  return h("div", null, row("X", "Not connected.", begin), area);
}

function xLinked(repaint: () => Promise<void>): HTMLElement {
  const summary = h("div", null);
  const err = h("div", { "aria-live": "polite" });
  const file = h("input", { id: "x-archive", type: "file", accept: ".zip,.js,application/zip", multiple: true }) as HTMLInputElement;
  const upload = h("button", { class: "btn small", type: "button" }, "Upload");
  const showImport = async () => {
    try {
      const r = await api<{ counts: { following: number; followers: number }; pending: number; restored: { following: string[]; friends: string[]; friend_suggestions: string[] } }>("social", "/x/import");
      clear(
        summary,
        row(
          "Your X network",
          `${r.restored.following.length} reconnected, ${r.restored.friend_suggestions.length + r.restored.friends.length} friend ${r.restored.friend_suggestions.length + r.restored.friends.length === 1 ? "match" : "matches"}. ${r.pending} more aren't here yet; we'll tell you when they arrive.`,
          h("button", { class: "btn small ghost", type: "button", onclick: async () => (await api("social", "/x/import", { method: "DELETE" }), toast("Import undone"), void showImport()) }, "Undo import"),
        ),
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) clear(summary, h("p", { class: "dim" }, "Next: upload your follower list. In X, go to Settings → Your account → Download an archive of your data. When it arrives, upload the zip (or just data/follower.js and data/following.js)."));
      else clear(summary, errorBox(e));
    }
  };
  upload.addEventListener("click", async () => {
    clear(err);
    const files = [...(file.files ?? [])];
    if (!files.length) return clear(err, h("p", { class: "error" }, "Choose the zip or the two .js files first."));
    upload.setAttribute("disabled", "");
    try {
      const form = new FormData();
      for (const f of files) {
        if (/\.zip$/i.test(f.name)) form.append("archive", f);
        else if (/^follower/i.test(f.name)) form.append("follower", f);
        else if (/^following/i.test(f.name)) form.append("following", f);
      }
      const t = await accessToken();
      const res = await fetch(`${API_BASE.social}/x/archive`, { method: "POST", headers: t ? { authorization: `Bearer ${t}` } : {}, body: form });
      if (!res.ok) {
        const b = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(b?.error?.message ?? `Upload failed (${res.status}).`);
      }
      toast("Uploaded");
      await showImport();
    } catch (e) {
      clear(err, errorBox(e));
    } finally {
      upload.removeAttribute("disabled");
    }
  });
  void showImport();
  return h(
    "div",
    null,
    row("X", "Connected.", h("button", { class: "btn small ghost", type: "button", onclick: async () => (await api("social", "/x/link", { method: "DELETE" }), toast("X disconnected"), void repaint()) }, "Disconnect")),
    summary,
    h("div", { class: "set-row" }, h("div", null, h("b", null, "Follower list"), h("p", null, "The zip from X, or data/follower.js and data/following.js."), file), upload),
    err,
  );
}

/**
 * "Sign out everywhere": true once accounts revoked every session, or when this session was already gone (401: the
 * work is done). Anything else is the error, and the page stays signed in to show it (review 2026-10-19).
 */
export async function signOutEverywhere(call: () => Promise<unknown>): Promise<true | unknown> {
  try {
    await call();
    return true;
  } catch (e) {
    return needsSignIn(e) ? true : e;
  }
}
